import cloneDeep from 'lodash.clonedeep';
import { TargetModel } from 'data/target/target.types';
import { CaseModel } from 'data/case/case.types';
import { TEMPORARY_TARGET_ID_PREFIX } from './case-modeler';

type TargetsGroupedByTypeAndValue = {
  [primaryTypeValueHash:string] : {
    existingTargets: TargetModel[]
    newTargets: TargetModel[]
  }
}

const COMBINED_TARGET_PREFIX = 'COMBINED';
export function isCombinedTarget(caseTargetTag?: string): boolean {
  if (!caseTargetTag) return false;
  return caseTargetTag.startsWith(COMBINED_TARGET_PREFIX);
}

export function getFirstTargetTag(caseTargetTag?: string): string | null {
  if (!caseTargetTag) return null;
  let targetTag = caseTargetTag;
  if (isCombinedTarget(caseTargetTag)) {
    const typeAndID = caseTargetTag?.split('%')[1].split(',');
    [targetTag] = typeAndID;
  }
  return targetTag;
}

export function getTargetTags(caseTargetTag?: string): string[] | null {
  if (!caseTargetTag) return null;

  let targetTags: string[] = [];
  if (isCombinedTarget(caseTargetTag)) {
    const typeAndIDs = caseTargetTag?.split('%');
    typeAndIDs.shift(); // first item is COMBINED_TARGET_PREFIX
    targetTags = typeAndIDs.map((typeAndID) => (typeAndID.split(',')[0]));
  } else {
    targetTags.push(caseTargetTag);
  }
  return targetTags;
}

/**
 * When creating Circuit Switch Warrant, the warrant page shows MDN, CASEID, and MIN.  Verizon wants that if
 * operator enters the same value in MDN and MIN, create only one CASEID using the MDN value.
 * This function combines the CASEIDMDN and CASEIDMIN if they have the same value.
 *
 * @param cases
 */
export function combineTargetsWithSameTypeAndValue(
  cases: CaseModel[],
):void {

  cases.forEach((aCase) => {
    if (aCase.includeInWarrant === false) return;

    // Although this code can be applied to all duplicate targets, currently only Circuit Switch has this scenario.
    // Therefore run through this code for Circuit Switch only to minimize code complexity on other services.
    if (aCase.services.includes('GSMCDMA-2G-VOICE') === false) return;

    // create a data structure to organize the data first
    const targetsGroupedByTypeAndValue: TargetsGroupedByTypeAndValue = {};
    aCase.targets.forEach((target) => {
      // ignore targets with secondaryType since currently this only applies to 'CASEIDMIN', 'CASEIDMDN'
      if (target.secondaryType) {
        return;
      }

      const caseId = target.caseId?.id;
      if (caseId) {
        // restrict this to only work
        if (target.primaryType !== 'CASEIDMDN' && target.primaryType !== 'CASEIDMIN') return;
        const primaryType = target.actualPrimaryType ?? target.primaryType;
        const { primaryValue } = target;
        const primaryTypeValueHash = `${primaryType}:${primaryValue}`;

        // initialize the data structure
        if (!targetsGroupedByTypeAndValue[primaryTypeValueHash]) {
          targetsGroupedByTypeAndValue[primaryTypeValueHash] = {
            existingTargets: [],
            newTargets: [],
          };
        }

        if (target.id?.startsWith(TEMPORARY_TARGET_ID_PREFIX)) {
          targetsGroupedByTypeAndValue[primaryTypeValueHash].newTargets.push(target);
        } else {
          targetsGroupedByTypeAndValue[primaryTypeValueHash].existingTargets.push(target);
        }
      }
    });

    // remove duplicate targets
    Object.keys(targetsGroupedByTypeAndValue).forEach((primaryTypeValueHash) => {
      const { newTargets, existingTargets } = targetsGroupedByTypeAndValue[primaryTypeValueHash];
      const targets = [...existingTargets, ...newTargets];
      if (targets.length > 1) {
        const { combinedTarget, combinedAllTargetAf } = createCombinedTarget(aCase, targets);
        removeTargets(aCase, targets);
        aCase.targets.push(combinedTarget);
        aCase.allTargetAfs?.push(combinedAllTargetAf);
      }
    });

  });
}

export function createCombinedTarget(aCase:CaseModel, targets:TargetModel[]) : {
  combinedTarget:TargetModel,
  combinedAllTargetAf: TargetModel } {
  const allTargetAfsHashByTargetType:{[primaryType:string]:TargetModel} = {};
  aCase.allTargetAfs?.forEach((allTargetAf) => {
    allTargetAfsHashByTargetType[allTargetAf.primaryType as string] = allTargetAf;
  });

  const combinedTarget = cloneDeep(targets[0]);
  let primaryType = `${COMBINED_TARGET_PREFIX}%${targets[0].primaryType},${targets[0].id}`;
  const combinedAllTargetAf = cloneDeep(allTargetAfsHashByTargetType[combinedTarget.primaryType as string]);
  targets.forEach((nextTarget, index) => {
    if (index === 0) return;
    primaryType = `${primaryType}%${nextTarget.primaryType},${nextTarget.id}`;
    mergeTargetAfs(combinedTarget, nextTarget);

    const nextAllTargetAf = allTargetAfsHashByTargetType[nextTarget.primaryType as string];
    mergeTargetAfs(combinedAllTargetAf, nextAllTargetAf);
  });

  combinedTarget.primaryType = primaryType;
  combinedTarget.actualPrimaryType = targets[0].actualPrimaryType;
  combinedAllTargetAf.primaryType = primaryType;

  return { combinedTarget, combinedAllTargetAf };
}

export function removeTargets(aCase: CaseModel, deleteTargets:TargetModel[]):void {
  const deleteTargetIds = deleteTargets.map((target) => (target.id));

  if (!aCase.targets) return;
  aCase.targets = aCase.targets.filter((target) => (!deleteTargetIds.includes(target.id)));
}

function mergeTargetAfs(target: TargetModel, nextTarget: TargetModel): void {
  const targetAftypes1 = new Set<string>();
  target.afs?.afIds.forEach((obj) => { targetAftypes1.add(obj.id as string); });

  nextTarget.afs?.afIds.forEach((obj) => {
    if (!targetAftypes1.has(obj.id as string)) {
      target.afs?.afIds.push(obj);
      targetAftypes1.add(obj.id as string);
    }
  });

  const targetAftypes2 = new Set();
  target.afs?.afAutowireTypes.forEach((obj) => { targetAftypes2.add(obj.name); });

  nextTarget.afs?.afAutowireTypes.forEach((obj) => {
    if (!targetAftypes2.has(obj.name)) {
      target.afs?.afAutowireTypes.push(obj);
      targetAftypes2.add(obj.name);
    }
  });
}

export function splitCombinedTarget(cases: CaseModel[]):void {
  cases.forEach((aCase) => {
    if (aCase.includeInWarrant === false) return;

    // Although this code can be applied to all duplicate targets, currently only Circuit Switch has this scenario.
    // Therefore run through this code for Circuit Switch only to minimize code complexity on other services.
    if (aCase.services.includes('GSMCDMA-2G-VOICE') === false) return;

    const allTargetAfsHashByTargetType:{[primaryType:string]:TargetModel} = {};
    aCase.allTargetAfs?.forEach((allTargetAf) => {
      allTargetAfsHashByTargetType[allTargetAf.primaryType as string] = allTargetAf;
    });

    const combinedTargets : TargetModel[] = [];
    const splittedTargets:TargetModel[] = [];
    aCase.targets.forEach((target) => {
      // only split if target is a combine target
      if (!target.primaryType?.startsWith(COMBINED_TARGET_PREFIX)) return;

      const primaryTypeIDs = target.primaryType?.split('%');
      primaryTypeIDs.forEach((primaryTypeID) => {
        if (primaryTypeID === COMBINED_TARGET_PREFIX) return;

        const splittedPrimaryTypeID = primaryTypeID.split(',') as string[];
        // Do not add to case new target with the same type and value of an existing target
        if (splittedPrimaryTypeID[1].startsWith(TEMPORARY_TARGET_ID_PREFIX) && splittedTargets.length > 0) return;

        const splittedTarget = cloneDeep(target);
        [splittedTarget.primaryType, splittedTarget.id] = splittedPrimaryTypeID;
        splittedTargets.push(splittedTarget);
        removeNotApplicableAfs(splittedTarget, allTargetAfsHashByTargetType[splittedTarget.primaryType]);
      });

      combinedTargets.push(target);
    });

    removeTargets(aCase, combinedTargets);
    // add splitted targets to warrant-case
    splittedTargets.forEach((splittedTarget) => {
      aCase.targets.push(splittedTarget);
    });
  });
}

export function removeNotApplicableAfs(target:TargetModel, allTargetAf:TargetModel):void {
  if (!target.afs) return;
  if (!allTargetAf.afs) {
    target.afs = allTargetAf.afs;
    return;
  }

  const afIds = new Set<string>();
  allTargetAf.afs?.afIds.forEach((afId) => {
    if (afId.id) {
      afIds.add(afId.id);
    }
  });
  target.afs.afIds = target.afs.afIds.filter((afId) => (afId.id ? afIds.has(afId.id) : false));

  const afAutowireTypes = new Set<string>();
  allTargetAf.afs?.afAutowireTypes.forEach((autowireType) => {
    if (autowireType.id) {
      afAutowireTypes.add(autowireType.id);
    }
  });
  target.afs.afAutowireTypes = target.afs.afAutowireTypes.filter((afAutowireType) => (
    afAutowireType.id ? afAutowireTypes.has(afAutowireType.id) : false));
}
